import React, { useState, useEffect } from 'react';
import { supabase } from './supabase';
import { PLAN_LIMITS, getUsageCount, getResetDate } from './config/planLimits';

const PORTAL_URL =
  'https://ubtjzzsgcvxrkhqlcrlr.supabase.co/functions/v1/create-portal-session';

const PLAN_HIERARCHY = { professional: 1, business: 2, starter: 1, pro: 1, team: 2 };

const PLAN_DETAILS = {
  professional: { label: 'Professional', price: '$399/mo',  color: 'var(--accent)' },
  business:     { label: 'Business',     price: '$799/mo',  color: 'var(--green)'  },
};

const NEXT_PLAN = { professional: 'business' };

const NEXT_PLAN_FEATURES = {
  business: ['SOC 2, ISO 27001, HIPAA, NIST CSF, PCI DSS, FedRAMP', 'AI assistant (200 messages/month)', 'Unlimited users', 'Dedicated account manager'],
};


const FEATURE_GROUPS = [
  {
    label: 'Professional Features',
    features: [
      { label: 'Compliance Dashboard & Readiness Score',    plan: 'professional' },
      { label: 'Self-Assessment Questionnaire',             plan: 'professional' },
      { label: 'Gap Analysis Dashboard',                    plan: 'professional' },
      { label: 'Remediation Task Tracker',                  plan: 'professional' },
      { label: 'Evidence Locker',                           plan: 'professional' },
      { label: 'Company Profile Management',                plan: 'professional' },
      { label: 'CMMC Level 1, 2 & 3 Assessment',           plan: 'professional' },
      { label: 'NIST SP 800-171 Assessment',                plan: 'professional' },
      { label: 'SSP Document Reader & AI Import',           plan: 'professional' },
      { label: 'AI Compliance Assistant (50/mo)',           plan: 'professional' },
      { label: 'Policy Template Library',                   plan: 'professional' },
      { label: 'Auditor Access for C3PAOs',                 plan: 'professional' },
      { label: 'Annual Affirmation Tracker',                plan: 'professional' },
      { label: 'Reports & SSP PDF Export',                  plan: 'professional' },
      { label: 'Up to 3 team members',                      plan: 'professional' },
    ],
  },
  {
    label: 'Business Features',
    features: [
      { label: 'SOC 2 Type II Assessment (87 controls)',   plan: 'business' },
      { label: 'ISO 27001:2022 Assessment (93 controls)',  plan: 'business' },
      { label: 'HIPAA Security Rule Assessment',           plan: 'business' },
      { label: 'NIST CSF 2.0 Assessment',                  plan: 'business' },
      { label: 'PCI DSS v4.0 & FedRAMP Moderate',         plan: 'business' },
      { label: 'AI Compliance Assistant (200/mo)',         plan: 'business' },
      { label: 'Unlimited team seats',                     plan: 'business' },
      { label: 'Dedicated account manager',                plan: 'business' },
      { label: 'Phone & priority email support',           plan: 'business' },
    ],
  },
];

// ── Tab: Profile ──────────────────────────────────────────────────────────────
function ProfileTab({ session, profile, onSave }) {
  const [form, setForm] = useState({
    company_name: '', cage_code: '', contact_name: '',
    phone: '', address: '', city: '', state: '', zip: '',
    employees: '', website: '',
  });
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (profile) {
      setForm({
        company_name:  profile.company_name  || '',
        cage_code:     profile.cage_code     || '',
        contact_name:  profile.contact_name  || '',
        phone:         profile.phone         || '',
        address:       profile.address       || '',
        city:          profile.city          || '',
        state:         profile.state         || '',
        zip:           profile.zip           || '',
        employees:     profile.employees     || '',
        website:       profile.website       || '',
      });
    }
  }, [profile]);

  async function handleSave() {
    await onSave(form);
    setSaved(true);
    setTimeout(() => setSaved(false), 3000);
  }

  const inputStyle = {
    width: '100%', background: 'var(--surface)',
    border: '1px solid var(--border2)', borderRadius: 6,
    padding: '9px 12px', fontSize: 13, color: 'var(--text)',
    fontFamily: 'var(--font)', outline: 'none', transition: 'border-color 0.15s',
    boxSizing: 'border-box',
  };
  const labelStyle = {
    fontSize: 11, color: 'var(--text3)', marginBottom: 5,
    display: 'block', textTransform: 'uppercase', letterSpacing: '0.06em',
  };

  const sections = [
    {
      title: 'Company Information',
      fields: [
        { label: 'Company Name',        key: 'company_name',  placeholder: 'e.g. Acme Defense Co.',        full: true  },
        { label: 'CAGE Code',           key: 'cage_code',     placeholder: 'e.g. 7ACB4',                   full: false },
        { label: 'Number of Employees', key: 'employees',     placeholder: 'e.g. 45',                      full: false },
        { label: 'Website',             key: 'website',       placeholder: 'e.g. https://acmedefense.com', full: true  },
      ],
    },
    {
      title: 'Primary Contact',
      fields: [
        { label: 'Contact Name',  key: 'contact_name', placeholder: 'e.g. John Smith',      full: false },
        { label: 'Phone Number',  key: 'phone',         placeholder: 'e.g. (410) 555-0123', full: false },
      ],
    },
    {
      title: 'Company Address',
      fields: [
        { label: 'Street Address', key: 'address', placeholder: 'e.g. 123 Defense Blvd', full: true  },
        { label: 'City',           key: 'city',    placeholder: 'e.g. Baltimore',         full: false },
        { label: 'State',          key: 'state',   placeholder: 'e.g. MD',                full: false },
        { label: 'ZIP Code',       key: 'zip',     placeholder: 'e.g. 21201',             full: false },
      ],
    },
  ];

  return (
    <div>
      {/* Avatar + save row */}
      <div style={{
        background: 'var(--bg2)', border: '1px solid var(--border)',
        borderRadius: 10, padding: 20, marginBottom: 20,
        display: 'flex', alignItems: 'center', gap: 16,
      }}>
        <div style={{
          width: 56, height: 56, borderRadius: '50%', flexShrink: 0,
          background: 'linear-gradient(135deg, #2D7DD2, #0D3F73)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 20, fontWeight: 700, color: '#fff',
        }}>
          {(form.company_name || 'C').charAt(0).toUpperCase()}
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 15, fontWeight: 600 }}>
            {form.company_name || 'Your Company Name'}
          </div>
          <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 2 }}>
            {session?.user?.email}
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
            {form.cage_code && (
              <span style={{
                fontSize: 10, fontFamily: 'var(--mono)', color: 'var(--accent)',
                background: 'var(--blue-bg)', padding: '2px 8px', borderRadius: 4,
                border: '1px solid rgba(45,125,210,0.2)',
              }}>CAGE: {form.cage_code}</span>
            )}
            {form.employees && (
              <span style={{
                fontSize: 10, color: 'var(--text3)',
                background: 'var(--surface)', padding: '2px 8px', borderRadius: 4,
                border: '1px solid var(--border)',
              }}>{form.employees} employees</span>
            )}
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {saved && (
            <span style={{
              fontSize: 12, color: 'var(--green)', background: 'var(--green-bg)',
              border: '1px solid var(--green)', padding: '5px 12px', borderRadius: 6,
            }}>Saved</span>
          )}
          <button onClick={handleSave} style={{
            fontSize: 12, fontWeight: 600, padding: '8px 18px', borderRadius: 6,
            cursor: 'pointer', border: '1px solid var(--accent)',
            background: 'var(--accent)', color: '#fff', fontFamily: 'var(--font)',
          }}>Save Profile</button>
        </div>
      </div>

      {sections.map(section => (
        <div key={section.title} style={{
          background: 'var(--bg2)', border: '1px solid var(--border)',
          borderRadius: 10, padding: 20, marginBottom: 16,
        }}>
          <div style={{
            fontSize: 13, fontWeight: 600, marginBottom: 14,
            paddingBottom: 10, borderBottom: '1px solid var(--border)',
          }}>{section.title}</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
            {section.fields.map(field => (
              <div key={field.key} style={{ gridColumn: field.full ? '1 / -1' : 'auto' }}>
                <label style={labelStyle}>{field.label}</label>
                <input
                  type="text"
                  placeholder={field.placeholder}
                  value={form[field.key]}
                  onChange={e => setForm(prev => ({ ...prev, [field.key]: e.target.value }))}
                  style={inputStyle}
                  onFocus={e => e.target.style.borderColor = 'var(--accent)'}
                  onBlur={e => e.target.style.borderColor = 'var(--border2)'}
                />
              </div>
            ))}
          </div>
        </div>
      ))}

      {/* Account info */}
      <div style={{
        background: 'var(--bg2)', border: '1px solid var(--border)',
        borderRadius: 10, padding: 20, marginBottom: 16,
      }}>
        <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 14, paddingBottom: 10, borderBottom: '1px solid var(--border)' }}>
          Account Information
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
          {[
            { label: 'Account Email',   value: session?.user?.email || 'Not set' },
            { label: 'Account Created', value: session?.user?.created_at ? new Date(session.user.created_at).toLocaleDateString() : 'Unknown' },
            { label: 'User ID',         value: session?.user?.id ? session.user.id.substring(0, 16) + '…' : 'Unknown', mono: true },
          ].map(item => (
            <div key={item.label}>
              <label style={labelStyle}>{item.label}</label>
              <div style={{
                background: 'var(--surface)', border: '1px solid var(--border)',
                borderRadius: 6, padding: '9px 12px', fontSize: 13, color: 'var(--text2)',
                fontFamily: item.mono ? 'var(--mono)' : 'var(--font)',
              }}>{item.value}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── Tab: Subscription ─────────────────────────────────────────────────────────
function SubscriptionTab({ session, currentPlan = 'professional', onUpgrade }) {
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');

  const planKey    = currentPlan ? currentPlan.toLowerCase() : 'professional';
  const planInfo   = PLAN_DETAILS[planKey] || PLAN_DETAILS.professional;
  const planLevel  = PLAN_HIERARCHY[planKey] || 1;
  const nextPlanId = NEXT_PLAN[planKey];
  const nextPlan   = nextPlanId ? PLAN_DETAILS[nextPlanId] : null;
  const nextFeatures = nextPlanId ? (NEXT_PLAN_FEATURES[nextPlanId] || []) : [];

  async function handleManageSubscription() {
    setLoading(true); setError('');
    try {
      const response = await fetch(PORTAL_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${process.env.REACT_APP_SUPABASE_ANON_KEY}`,
        },
        body: JSON.stringify({ email: session?.user?.email, returnUrl: window.location.origin }),
      });
      const data = await response.json();
      if (data.error) { setError(data.error); setLoading(false); return; }
      window.location.href = data.url;
    } catch {
      setError('Could not connect to billing portal. Please try again.');
      setLoading(false);
    }
  }

  return (
    <div>
      {/* ── Action buttons ── */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>

        {/* Manage Subscription */}
        <div style={{ flex: 1, minWidth: 180 }}>
          <button
            onClick={handleManageSubscription}
            disabled={loading}
            style={{
              width: '100%', padding: '11px 18px', borderRadius: 8,
              fontSize: 13, fontWeight: 600,
              cursor: loading ? 'not-allowed' : 'pointer',
              border: '1px solid var(--border2)',
              background: 'var(--surface)', color: 'var(--text)',
              fontFamily: 'var(--font)', opacity: loading ? 0.7 : 1,
              transition: 'all 0.15s',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7,
            }}
            onMouseEnter={e => { if (!loading) { e.currentTarget.style.background = 'var(--surface2)'; e.currentTarget.style.borderColor = 'var(--accent)'; } }}
            onMouseLeave={e => { e.currentTarget.style.background = 'var(--surface)'; e.currentTarget.style.borderColor = 'var(--border2)'; }}
          >
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="1" y="4" width="22" height="16" rx="2"/><line x1="1" y1="10" x2="23" y2="10"/>
            </svg>
            {loading ? 'Opening portal…' : 'Manage Subscription'}
          </button>
          <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 6, lineHeight: 1.4 }}>
            View invoices, update payment method, or cancel your subscription
          </div>
        </div>

        {/* View All Plans — or Highest Plan badge */}
        <div style={{ flex: 1, minWidth: 180 }}>
          {planKey !== 'business' ? (
            <>
              <button
                onClick={onUpgrade}
                style={{
                  width: '100%', padding: '11px 18px', borderRadius: 8,
                  fontSize: 13, fontWeight: 600,
                  cursor: 'pointer',
                  border: '1px solid var(--accent)',
                  background: 'var(--accent)', color: '#fff',
                  fontFamily: 'var(--font)',
                  transition: 'background 0.15s',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7,
                }}
                onMouseEnter={e => e.currentTarget.style.background = '#2570C0'}
                onMouseLeave={e => e.currentTarget.style.background = 'var(--accent)'}
              >
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="18 15 12 9 6 15"/>
                </svg>
                View All Plans
              </button>
              <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 6, lineHeight: 1.4 }}>
                Unlock more frameworks, team members, and features
              </div>
            </>
          ) : (
            <div style={{
              width: '100%', padding: '11px 18px', borderRadius: 8,
              border: '1px solid #a78bfa40', background: '#a78bfa0D',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
            }}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#a78bfa" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
              </svg>
              <span style={{ fontSize: 13, fontWeight: 600, color: '#a78bfa' }}>Highest Plan</span>
            </div>
          )}
        </div>
      </div>

      {error && (
        <div style={{
          background: 'var(--red-bg)', border: '1px solid var(--red)',
          borderRadius: 6, padding: '10px 12px', fontSize: 12, color: 'var(--red)', marginBottom: 16,
        }}>{error}</div>
      )}

      {/* Current plan card */}
      <div style={{
        background: 'var(--bg2)', border: `2px solid ${planInfo.color}40`,
        borderRadius: 10, padding: 20, marginBottom: 16,
        position: 'relative', overflow: 'hidden',
      }}>
        {/* Top accent strip */}
        <div style={{
          position: 'absolute', top: 0, left: 0, right: 0, height: 3,
          background: planInfo.color,
        }} />
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <div style={{ fontSize: 10, color: 'var(--text3)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
              Current Plan
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
              <div style={{ fontSize: 24, fontWeight: 700, color: planInfo.color }}>{planInfo.label}</div>
              <span style={{
                fontSize: 10, fontWeight: 700, padding: '3px 9px', borderRadius: 20,
                background: 'var(--green-bg)', color: 'var(--green)', border: '1px solid var(--green)',
              }}>Active</span>
            </div>
            <div style={{ fontSize: 11, color: 'var(--text3)' }}>
              Billed monthly · {session?.user?.email}
            </div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: 26, fontWeight: 700, color: planInfo.color }}>{planInfo.price}</div>
            <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 2 }}>per month</div>
          </div>
        </div>
      </div>

      {/* Direct pricing page link */}
      <div style={{
        background: 'var(--surface)', border: '1px solid var(--border)',
        borderRadius: 8, padding: '14px 16px', marginBottom: 20,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
      }}>
        <div>
          <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text)', marginBottom: 2 }}>
            See all plans and pricing
          </div>
          <div style={{ fontSize: 11, color: 'var(--text3)' }}>
            Compare features and choose the right plan for your organization
          </div>
        </div>
        <button
          onClick={onUpgrade}
          style={{
            padding: '9px 18px', borderRadius: 6, fontSize: 12, fontWeight: 600,
            cursor: 'pointer', border: '1px solid var(--accent)',
            background: 'var(--accent)', color: '#fff',
            fontFamily: 'var(--font)', flexShrink: 0,
            transition: 'background 0.15s',
            display: 'flex', alignItems: 'center', gap: 6,
          }}
          onMouseEnter={e => e.currentTarget.style.background = '#2570C0'}
          onMouseLeave={e => e.currentTarget.style.background = 'var(--accent)'}
        >
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="18 15 12 9 6 15"/>
          </svg>
          View Pricing Plans
        </button>
      </div>

      {/* Next plan teaser */}
      {nextPlan && (
        <div style={{
          background: `linear-gradient(135deg, ${nextPlan.color}0D, ${nextPlan.color}05)`,
          border: `1px solid ${nextPlan.color}30`,
          borderRadius: 10, padding: 20, marginBottom: 16,
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 14 }}>
            <div>
              <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text)', marginBottom: 3 }}>
                Upgrade to <span style={{ color: nextPlan.color }}>{nextPlan.label}</span>
              </div>
              <div style={{ fontSize: 11, color: 'var(--text3)' }}>
                {nextPlan.price} · Everything in {planInfo.label}, plus:
              </div>
            </div>
            <button
              onClick={onUpgrade}
              style={{
                padding: '8px 16px', borderRadius: 7, fontSize: 12, fontWeight: 600,
                cursor: 'pointer', border: `1px solid ${nextPlan.color}`,
                background: nextPlan.color, color: '#fff',
                fontFamily: 'var(--font)', flexShrink: 0,
                transition: 'opacity 0.15s',
              }}
              onMouseEnter={e => e.currentTarget.style.opacity = '0.85'}
              onMouseLeave={e => e.currentTarget.style.opacity = '1'}
            >
              Upgrade Now
            </button>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '5px 12px' }}>
            {nextFeatures.map(f => (
              <div key={f} style={{ display: 'flex', alignItems: 'flex-start', gap: 7, fontSize: 11, color: 'var(--text2)' }}>
                <span style={{ color: nextPlan.color, flexShrink: 0, fontWeight: 700, marginTop: 1 }}>+</span>
                <span>{f}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Features included */}
      <div style={{
        background: 'var(--bg2)', border: '1px solid var(--border)',
        borderRadius: 10, padding: 20, marginBottom: 16,
      }}>
        <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 16, paddingBottom: 10, borderBottom: '1px solid var(--border)' }}>
          Features included in your plan
        </div>
        {FEATURE_GROUPS.map(group => {
          const groupLevel    = PLAN_HIERARCHY[group.features[0]?.plan] || 1;
          const groupIncluded = groupLevel <= planLevel;
          return (
            <div key={group.label} style={{ marginBottom: 16 }}>
              <div style={{
                fontSize: 10, fontWeight: 600, color: 'var(--text3)',
                textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 8,
                display: 'flex', alignItems: 'center', gap: 6,
              }}>
                <span style={{
                  width: 6, height: 6, borderRadius: '50%', display: 'inline-block',
                  background: groupIncluded ? 'var(--green)' : 'var(--border2)',
                }} />
                {group.label}
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '5px 12px' }}>
                {group.features.map(f => {
                  const featureLevel   = PLAN_HIERARCHY[f.plan] || 1;
                  const featureIncluded = featureLevel <= planLevel;
                  const unlockPlan     = !featureIncluded ? PLAN_DETAILS[f.plan] : null;
                  return (
                    <div key={f.label} style={{
                      display: 'flex', alignItems: 'flex-start', gap: 7,
                      fontSize: 12, color: featureIncluded ? 'var(--text2)' : 'var(--text3)',
                      opacity: featureIncluded ? 1 : 0.65,
                    }}>
                      <div style={{
                        width: 14, height: 14, borderRadius: '50%', flexShrink: 0, marginTop: 1,
                        background: featureIncluded ? 'var(--green-bg)' : 'var(--surface2)',
                        border: `1px solid ${featureIncluded ? 'var(--green)' : 'var(--border)'}`,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 8, fontWeight: 700,
                        color: featureIncluded ? 'var(--green)' : 'var(--text3)',
                      }}>
                        {featureIncluded ? '✓' : (
                          <svg width="7" height="8" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                            <rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0110 0v4"/>
                          </svg>
                        )}
                      </div>
                      <span style={{ lineHeight: 1.4 }}>
                        {f.label}
                        {unlockPlan && (
                          <span style={{ marginLeft: 4, fontSize: 9, fontWeight: 600, color: unlockPlan.color, fontFamily: 'var(--mono)' }}>
                            [{unlockPlan.label}]
                          </span>
                        )}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      {/* Billing info */}
      <div style={{
        background: 'var(--bg2)', border: '1px solid var(--border)',
        borderRadius: 10, padding: 20, marginBottom: 16,
      }}>
        <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 14, paddingBottom: 10, borderBottom: '1px solid var(--border)' }}>
          Billing Information
        </div>
        {[
          { label: 'Account Email',  value: session?.user?.email || 'Not set'        },
          { label: 'Billing Cycle',  value: 'Monthly'                                },
          { label: 'Payment Method', value: 'Manage in billing portal'               },
          { label: 'Next Invoice',   value: 'View in billing portal'                 },
        ].map(item => (
          <div key={item.label} style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            padding: '9px 0', borderBottom: '1px solid var(--border)', fontSize: 12,
          }}>
            <span style={{ color: 'var(--text3)' }}>{item.label}</span>
            <span style={{ fontWeight: 500, color: 'var(--text)' }}>{item.value}</span>
          </div>
        ))}
      </div>

      {/* Cancel */}
      <div style={{
        background: 'var(--bg2)', border: '1px solid var(--border)',
        borderRadius: 10, padding: 20,
      }}>
        <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6 }}>Need to cancel?</div>
        <div style={{ fontSize: 12, color: 'var(--text3)', lineHeight: 1.6, marginBottom: 14 }}>
          You can cancel at any time from the billing portal. Access continues until the end of your billing period.
        </div>
        <button
          onClick={handleManageSubscription}
          disabled={loading}
          style={{
            fontSize: 12, fontWeight: 500, padding: '7px 14px', borderRadius: 6,
            cursor: 'pointer', border: '1px solid var(--red)',
            background: 'transparent', color: 'var(--red)',
            fontFamily: 'var(--font)', transition: 'all 0.15s',
          }}
          onMouseEnter={e => { e.currentTarget.style.background = 'var(--red)'; e.currentTarget.style.color = '#fff'; }}
          onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'var(--red)'; }}
        >
          Cancel Subscription
        </button>
      </div>
    </div>
  );
}

// ── Tab: Security ─────────────────────────────────────────────────────────────
function SecurityTab({ session }) {
  const [newPassword,     setNewPassword]     = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [pwLoading,       setPwLoading]       = useState(false);
  const [pwError,         setPwError]         = useState('');
  const [pwSuccess,       setPwSuccess]       = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteText,      setDeleteText]      = useState('');

  // MFA state
  const [mfaEnabled,   setMfaEnabled]   = useState(false);
  const [mfaLoading,   setMfaLoading]   = useState(true);
  const [enrollStep,   setEnrollStep]   = useState(null); // null | 'verify'
  const [qrCode,       setQrCode]       = useState('');
  const [mfaSecret,    setMfaSecret]    = useState('');
  const [factorId,     setFactorId]     = useState('');
  const [verifyCode,   setVerifyCode]   = useState('');
  const [mfaError,     setMfaError]     = useState('');


  // Check MFA enrollment status on mount
  useEffect(() => {
    async function checkMFA() {
      try {
        const { data } = await supabase.auth.mfa.listFactors();
        const verified = (data?.totp || []).find(f => f.status === 'verified');
        if (verified) { setMfaEnabled(true); setFactorId(verified.id); }
      } catch {}
      setMfaLoading(false);
    }
    checkMFA();
  }, []);

  async function enrollMFA() {
    setMfaError(''); setMfaLoading(true);
    const { data, error } = await supabase.auth.mfa.enroll({
      factorType: 'totp',
      friendlyName: 'TheClearAudit Authenticator',
    });
    setMfaLoading(false);
    if (error) { setMfaError(error.message); return; }
    setFactorId(data.id);
    setQrCode(data.totp.qr_code);
    setMfaSecret(data.totp.secret);
    setEnrollStep('verify');
  }

  async function verifyMFA() {
    setMfaError(''); setMfaLoading(true);
    try {
      const { data: challengeData, error: cErr } = await supabase.auth.mfa.challenge({ factorId });
      if (cErr) { setMfaError(cErr.message); setMfaLoading(false); return; }
      const { error } = await supabase.auth.mfa.verify({
        factorId, challengeId: challengeData.id, code: verifyCode,
      });
      if (error) { setMfaError('Invalid code. Please try again.'); setMfaLoading(false); return; }
      setMfaEnabled(true); setEnrollStep(null); setVerifyCode(''); setQrCode(''); setMfaSecret('');
    } catch (e) {
      setMfaError('Verification failed. Please try again.');
    }
    setMfaLoading(false);
  }

  async function disableMFA() {
    if (!window.confirm('Are you sure you want to disable two-factor authentication? This will make your account less secure.')) return;
    setMfaLoading(true);
    const { error } = await supabase.auth.mfa.unenroll({ factorId });
    setMfaLoading(false);
    if (error) { setMfaError(error.message); return; }
    setMfaEnabled(false); setFactorId('');
  }

  function cancelEnroll() {
    if (factorId) supabase.auth.mfa.unenroll({ factorId }).catch(() => {});
    setEnrollStep(null); setQrCode(''); setMfaSecret(''); setVerifyCode(''); setMfaError(''); setFactorId('');
  }

  async function handleChangePassword() {
    setPwError(''); setPwSuccess('');
    if (!newPassword || newPassword.length < 8) {
      setPwError('Password must be at least 8 characters.'); return;
    }
    if (newPassword !== confirmPassword) {
      setPwError('Passwords do not match.'); return;
    }
    setPwLoading(true);
    const { error } = await supabase.auth.updateUser({ password: newPassword });
    if (error) setPwError(error.message);
    else { setPwSuccess('Password updated successfully.'); setNewPassword(''); setConfirmPassword(''); }
    setPwLoading(false);
  }

  const inputStyle = {
    width: '100%', background: 'var(--surface)', border: '1px solid var(--border2)',
    borderRadius: 6, padding: '9px 12px', fontSize: 13, color: 'var(--text)',
    fontFamily: 'var(--font)', outline: 'none', transition: 'border-color 0.15s',
    boxSizing: 'border-box',
  };
  const labelStyle = { fontSize: 11, color: 'var(--text3)', marginBottom: 5, display: 'block', textTransform: 'uppercase', letterSpacing: '0.06em' };

  return (
    <div>
      {/* Change password */}
      <div style={{
        background: 'var(--bg2)', border: '1px solid var(--border)',
        borderRadius: 10, padding: 20, marginBottom: 16,
      }}>
        <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 14, paddingBottom: 10, borderBottom: '1px solid var(--border)' }}>
          Change Password
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 14 }}>
          <div>
            <label style={labelStyle}>New Password</label>
            <input
              type="password" value={newPassword}
              onChange={e => setNewPassword(e.target.value)}
              placeholder="Minimum 8 characters" style={inputStyle}
              onFocus={e => e.target.style.borderColor = 'var(--accent)'}
              onBlur={e => e.target.style.borderColor = 'var(--border2)'}
            />
          </div>
          <div>
            <label style={labelStyle}>Confirm Password</label>
            <input
              type="password" value={confirmPassword}
              onChange={e => setConfirmPassword(e.target.value)}
              placeholder="Re-enter new password" style={inputStyle}
              onFocus={e => e.target.style.borderColor = 'var(--accent)'}
              onBlur={e => e.target.style.borderColor = 'var(--border2)'}
            />
          </div>
        </div>
        {pwError && (
          <div style={{ background: 'var(--red-bg)', border: '1px solid var(--red)', borderRadius: 6, padding: '8px 12px', fontSize: 12, color: 'var(--red)', marginBottom: 12 }}>
            {pwError}
          </div>
        )}
        {pwSuccess && (
          <div style={{ background: 'var(--green-bg)', border: '1px solid var(--green)', borderRadius: 6, padding: '8px 12px', fontSize: 12, color: 'var(--green)', marginBottom: 12 }}>
            {pwSuccess}
          </div>
        )}
        <button
          onClick={handleChangePassword}
          disabled={pwLoading}
          style={{
            fontSize: 12, fontWeight: 600, padding: '8px 16px', borderRadius: 6,
            cursor: pwLoading ? 'not-allowed' : 'pointer',
            border: '1px solid var(--accent)', background: 'var(--accent)',
            color: '#fff', fontFamily: 'var(--font)', opacity: pwLoading ? 0.7 : 1,
          }}
        >
          {pwLoading ? 'Updating…' : 'Update Password'}
        </button>
      </div>

      {/* 2FA */}
      <div style={{
        background: 'var(--bg2)', border: '1px solid var(--border)',
        borderRadius: 10, padding: 20, marginBottom: 16,
      }}>
        <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 14, paddingBottom: 10, borderBottom: '1px solid var(--border)' }}>
          Two-Factor Authentication
        </div>

        {mfaError && (
          <div style={{ background: 'var(--red-bg)', border: '1px solid var(--red)', borderRadius: 6, padding: '8px 12px', fontSize: 12, color: 'var(--red)', marginBottom: 12 }}>
            {mfaError}
          </div>
        )}

        {mfaLoading && enrollStep === null ? (
          <div style={{ fontSize: 12, color: 'var(--text3)' }}>Checking 2FA status…</div>
        ) : mfaEnabled && enrollStep === null ? (
          /* Enabled state */
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
              <div>
                <div style={{ fontSize: 12, fontWeight: 500, color: 'var(--text)', marginBottom: 3 }}>
                  Authenticator App
                </div>
                <div style={{ fontSize: 11, color: 'var(--text3)' }}>
                  TOTP authentication is active. Use Google Authenticator or Authy to log in.
                </div>
              </div>
              <span style={{
                fontSize: 10, fontWeight: 700, padding: '4px 10px', borderRadius: 6,
                background: 'var(--green-bg)', color: 'var(--green)', border: '1px solid var(--green)',
              }}>2FA Enabled</span>
            </div>
            <button
              onClick={disableMFA}
              disabled={mfaLoading}
              style={{
                fontSize: 12, fontWeight: 500, padding: '7px 14px', borderRadius: 6,
                cursor: mfaLoading ? 'not-allowed' : 'pointer', border: '1px solid var(--red)',
                background: 'transparent', color: 'var(--red)', fontFamily: 'var(--font)',
                transition: 'all 0.15s', opacity: mfaLoading ? 0.6 : 1,
              }}
              onMouseEnter={e => { e.currentTarget.style.background = 'var(--red)'; e.currentTarget.style.color = '#fff'; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'var(--red)'; }}
            >
              Disable 2FA
            </button>
          </div>
        ) : enrollStep === 'verify' ? (
          /* Enrollment flow */
          <div>
            <div style={{ fontSize: 12, color: 'var(--text2)', lineHeight: 1.6, marginBottom: 16 }}>
              Scan this QR code with <strong>Google Authenticator</strong> or <strong>Authy</strong>, then enter the 6-digit code to confirm.
            </div>
            {qrCode && (
              <div style={{ marginBottom: 16, display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 12 }}>
                <img
                  src={qrCode}
                  alt="2FA QR Code"
                  style={{ width: 160, height: 160, borderRadius: 10, border: '2px solid var(--border2)', background: '#fff' }}
                />
                <div style={{ background: 'var(--surface)', border: '1px solid var(--border2)', borderRadius: 6, padding: '10px 12px' }}>
                  <div style={{ fontSize: 10, color: 'var(--text3)', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Manual entry code</div>
                  <div style={{ fontSize: 12, fontFamily: 'var(--mono)', color: 'var(--accent)', letterSpacing: '0.08em', wordBreak: 'break-all' }}>
                    {mfaSecret}
                  </div>
                </div>
              </div>
            )}
            <div style={{ marginBottom: 14 }}>
              <label style={labelStyle}>Verification Code</label>
              <input
                type="text"
                inputMode="numeric"
                maxLength={6}
                placeholder="000000"
                value={verifyCode}
                onChange={e => setVerifyCode(e.target.value.replace(/\D/g, ''))}
                style={{ ...inputStyle, width: 160, fontFamily: 'var(--mono)', letterSpacing: '0.2em', fontSize: 18, textAlign: 'center' }}
                onFocus={e => e.target.style.borderColor = 'var(--accent)'}
                onBlur={e => e.target.style.borderColor = 'var(--border2)'}
              />
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button
                onClick={verifyMFA}
                disabled={mfaLoading || verifyCode.length < 6}
                style={{
                  fontSize: 12, fontWeight: 600, padding: '8px 18px', borderRadius: 6,
                  cursor: mfaLoading || verifyCode.length < 6 ? 'not-allowed' : 'pointer',
                  border: '1px solid var(--accent)', background: 'var(--accent)',
                  color: '#fff', fontFamily: 'var(--font)',
                  opacity: mfaLoading || verifyCode.length < 6 ? 0.6 : 1,
                }}
              >
                {mfaLoading ? 'Verifying…' : 'Verify and Enable'}
              </button>
              <button
                onClick={cancelEnroll}
                style={{
                  fontSize: 12, padding: '8px 14px', borderRadius: 6,
                  cursor: 'pointer', border: '1px solid var(--border2)',
                  background: 'transparent', color: 'var(--text3)', fontFamily: 'var(--font)',
                }}
              >Cancel</button>
            </div>
          </div>
        ) : (
          /* Not enrolled */
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <div style={{ fontSize: 12, fontWeight: 500, color: 'var(--text)', marginBottom: 3 }}>
                Authenticator App
              </div>
              <div style={{ fontSize: 11, color: 'var(--text3)' }}>
                Adds an extra layer of security using Google Authenticator or Authy
              </div>
            </div>
            <button
              onClick={enrollMFA}
              disabled={mfaLoading}
              style={{
                fontSize: 12, fontWeight: 600, padding: '7px 14px', borderRadius: 6,
                cursor: mfaLoading ? 'not-allowed' : 'pointer',
                border: '1px solid var(--accent)', background: 'var(--accent)',
                color: '#fff', fontFamily: 'var(--font)', flexShrink: 0, marginLeft: 16,
                opacity: mfaLoading ? 0.6 : 1,
              }}
            >
              Enable 2FA
            </button>
          </div>
        )}
      </div>

      {/* Session info */}
      <div style={{
        background: 'var(--bg2)', border: '1px solid var(--border)',
        borderRadius: 10, padding: 20, marginBottom: 16,
      }}>
        <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 14, paddingBottom: 10, borderBottom: '1px solid var(--border)' }}>
          Active Session
        </div>
        <div style={{
          padding: '12px 14px', background: 'var(--surface)', borderRadius: 8,
          border: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        }}>
          <div>
            <div style={{ fontSize: 12, fontWeight: 500, color: 'var(--text)', marginBottom: 2 }}>
              Current Browser Session
            </div>
            <div style={{ fontSize: 11, color: 'var(--text3)' }}>
              {session?.user?.email} · Signed in
            </div>
            <div style={{ fontSize: 10, color: 'var(--text3)', marginTop: 2, fontFamily: 'var(--mono)' }}>
              Last login: {session?.user?.last_sign_in_at
                ? new Date(session.user.last_sign_in_at).toLocaleString()
                : 'Unknown'}
            </div>
          </div>
          <span style={{
            fontSize: 10, fontWeight: 700, padding: '3px 8px', borderRadius: 6,
            background: 'var(--green-bg)', color: 'var(--green)', border: '1px solid var(--green)',
          }}>Active</span>
        </div>
      </div>

      {/* Danger zone */}
      <div style={{
        background: 'var(--bg2)', border: '1px solid var(--red)',
        borderRadius: 10, padding: 20,
      }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--red)', marginBottom: 10 }}>
          Danger Zone
        </div>
        {!showDeleteConfirm ? (
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <div style={{ fontSize: 12, fontWeight: 500, color: 'var(--text)', marginBottom: 2 }}>Delete Account</div>
              <div style={{ fontSize: 11, color: 'var(--text3)' }}>
                Permanently delete your account and all compliance data. This cannot be undone.
              </div>
            </div>
            <button
              onClick={() => setShowDeleteConfirm(true)}
              style={{
                fontSize: 12, fontWeight: 500, padding: '7px 14px', borderRadius: 6,
                cursor: 'pointer', border: '1px solid var(--red)',
                background: 'transparent', color: 'var(--red)',
                fontFamily: 'var(--font)', flexShrink: 0, marginLeft: 16,
              }}
              onMouseEnter={e => { e.currentTarget.style.background = 'var(--red)'; e.currentTarget.style.color = '#fff'; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'var(--red)'; }}
            >Delete Account</button>
          </div>
        ) : (
          <div>
            <div style={{ fontSize: 12, color: 'var(--text2)', lineHeight: 1.6, marginBottom: 12 }}>
              Type <strong>DELETE</strong> to confirm account deletion. This action is permanent and cannot be undone.
            </div>
            <input
              type="text" value={deleteText}
              onChange={e => setDeleteText(e.target.value)}
              placeholder="Type DELETE to confirm"
              style={{
                width: '100%', padding: '9px 12px', borderRadius: 6, marginBottom: 12,
                border: '1px solid var(--red)', background: 'var(--surface)',
                color: 'var(--text)', fontSize: 13, fontFamily: 'var(--mono)',
                outline: 'none', boxSizing: 'border-box',
              }}
            />
            <div style={{ display: 'flex', gap: 10 }}>
              <button
                onClick={() => { setShowDeleteConfirm(false); setDeleteText(''); }}
                style={{
                  fontSize: 12, padding: '7px 14px', borderRadius: 6,
                  cursor: 'pointer', border: '1px solid var(--border2)',
                  background: 'transparent', color: 'var(--text3)', fontFamily: 'var(--font)',
                }}
              >Cancel</button>
              <button
                disabled={deleteText !== 'DELETE'}
                style={{
                  fontSize: 12, fontWeight: 600, padding: '7px 14px', borderRadius: 6,
                  cursor: deleteText === 'DELETE' ? 'pointer' : 'not-allowed',
                  border: '1px solid var(--red)', background: 'var(--red)', color: '#fff',
                  fontFamily: 'var(--font)', opacity: deleteText === 'DELETE' ? 1 : 0.4,
                }}
              >Confirm Delete</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Tab: Notifications ────────────────────────────────────────────────────────
const NOTIFICATION_ITEMS = [
  { key: 'deadline_reminders',   label: 'Compliance Deadline Reminders',    sub: 'Get notified 30 days before assessment deadlines',          defaultOn: true  },
  { key: 'task_reminders',       label: 'Task Due Date Reminders',          sub: 'Receive alerts when remediation tasks are approaching due',  defaultOn: true  },
  { key: 'weekly_digest',        label: 'Weekly Compliance Digest',         sub: 'Summary of your compliance posture every Monday',           defaultOn: false },
  { key: 'framework_announcements', label: 'New Framework Announcements',   sub: 'Be notified when new frameworks become available',          defaultOn: false },
  { key: 'billing_notifications',label: 'Plan & Billing Notifications',     sub: 'Renewal reminders and invoice notifications',               defaultOn: true  },
];

function NotificationsTab({ session }) {
  const userId = session?.user?.id;

  const defaultPrefs = Object.fromEntries(NOTIFICATION_ITEMS.map(i => [i.key, i.defaultOn]));
  const [prefs,   setPrefs]   = useState(defaultPrefs);
  const [saving,  setSaving]  = useState(false);
  const [saved,   setSaved]   = useState(false);
  const [loadErr, setLoadErr] = useState('');

  useEffect(() => {
    if (!userId) return;
    supabase.from('profiles').select('notification_preferences').eq('id', userId).single()
      .then(({ data, error }) => {
        if (!error && data?.notification_preferences) {
          setPrefs(prev => ({ ...prev, ...data.notification_preferences }));
        }
      });
  }, [userId]);

  async function handleToggle(key) {
    const updated = { ...prefs, [key]: !prefs[key] };
    setPrefs(updated);
    setSaved(false);
    setSaving(true);
    setLoadErr('');
    const { error } = await supabase.from('profiles')
      .update({ notification_preferences: updated })
      .eq('id', userId);
    setSaving(false);
    if (error) { setLoadErr('Failed to save preferences.'); }
    else { setSaved(true); setTimeout(() => setSaved(false), 2500); }
  }

  return (
    <div>
      <div style={{
        background: 'var(--bg2)', border: '1px solid var(--border)',
        borderRadius: 10, padding: 20,
      }}>
        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          marginBottom: 16, paddingBottom: 10, borderBottom: '1px solid var(--border)',
        }}>
          <div style={{ fontSize: 13, fontWeight: 600 }}>Email Notification Preferences</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {saving && <span style={{ fontSize: 11, color: 'var(--text3)' }}>Saving…</span>}
            {saved  && <span style={{ fontSize: 11, color: 'var(--green)' }}>Saved</span>}
            {loadErr && <span style={{ fontSize: 11, color: 'var(--red)' }}>{loadErr}</span>}
          </div>
        </div>
        {NOTIFICATION_ITEMS.map((item, i) => (
          <div key={item.key} style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            padding: '14px 0',
            borderBottom: i < NOTIFICATION_ITEMS.length - 1 ? '1px solid var(--border)' : 'none',
          }}>
            <div style={{ flex: 1, paddingRight: 16 }}>
              <div style={{ fontSize: 12, fontWeight: 500, color: 'var(--text)', marginBottom: 3 }}>
                {item.label}
              </div>
              <div style={{ fontSize: 11, color: 'var(--text3)' }}>{item.sub}</div>
            </div>
            <button
              onClick={() => handleToggle(item.key)}
              disabled={saving}
              style={{
                width: 36, height: 20, borderRadius: 10, border: 'none',
                cursor: saving ? 'not-allowed' : 'pointer',
                background: prefs[item.key] ? 'var(--accent)' : 'var(--border2)',
                position: 'relative', flexShrink: 0, transition: 'background 0.2s',
                padding: 0,
              }}
            >
              <div style={{
                position: 'absolute', top: 2,
                left: prefs[item.key] ? 18 : 2,
                width: 16, height: 16,
                borderRadius: '50%', background: '#fff', transition: 'left 0.2s',
              }} />
            </button>
          </div>
        ))}
      </div>
      <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 12, lineHeight: 1.6 }}>
        Notification emails are sent to <strong style={{ color: 'var(--text2)' }}>{session?.user?.email}</strong>.
        Security alerts (unusual login activity) are always sent and cannot be disabled.
      </div>
    </div>
  );
}

// ── Tab: Usage ────────────────────────────────────────────────────────────────
function UsageBar({ current, limit, color }) {
  if (limit === Infinity) return null;
  const pct      = limit === 0 ? 0 : Math.min(100, Math.round((current / limit) * 100));
  const barColor = color || (pct >= 95 ? 'var(--red)' : pct >= 80 ? 'var(--amber)' : 'var(--accent)');
  return (
    <div style={{ height: 6, background: 'var(--surface2)', borderRadius: 3, overflow: 'hidden', marginTop: 6 }}>
      <div style={{ height: '100%', width: `${pct}%`, background: barColor, borderRadius: 3, transition: 'width 0.4s ease' }} />
    </div>
  );
}

function UsageTab({ session, currentPlan, onUpgrade }) {
  const [aiCount,  setAiCount]  = useState(null);
  const [docCount, setDocCount] = useState(null);

  const plan   = (currentPlan || 'professional').toLowerCase();
  const limits = PLAN_LIMITS[plan] || PLAN_LIMITS.professional;
  const userId = session?.user?.id;

  useEffect(() => {
    if (!userId) return;
    Promise.all([
      getUsageCount(supabase, userId, 'ai_question'),
      getUsageCount(supabase, userId, 'document_upload'),
    ]).then(([ai, doc]) => { setAiCount(ai); setDocCount(doc); });
  }, [userId]);

  const aiLimit  = limits.ai_questions;
  const docLimit = limits.document_uploads;
  const resetDate = getResetDate();

  function pctOf(current, limit) {
    if (limit === 0 || limit === Infinity || current === null) return 0;
    return Math.min(100, Math.round((current / limit) * 100));
  }

  const aiPct  = pctOf(aiCount,  aiLimit);
  const docPct = pctOf(docCount, docLimit);

  const CARD_STYLE = {
    background: 'var(--bg2)', border: '1px solid var(--border)',
    borderRadius: 10, padding: '20px 22px', marginBottom: 14,
  };

  function limitLabel(limit) {
    if (limit === 0)        return 'Not available on this plan';
    if (limit === Infinity) return 'Unlimited';
    return `${limit} / month`;
  }

  return (
    <div>
      <div style={{ ...CARD_STYLE }}>
        <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 16, paddingBottom: 10, borderBottom: '1px solid var(--border)' }}>
          Monthly Usage
        </div>

        {/* AI Questions */}
        <div style={{ marginBottom: 20 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 2 }}>
            <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--text)' }}>AI Questions</span>
            <span style={{
              fontSize: 12, fontWeight: 600,
              color: aiLimit === 0 ? 'var(--text3)' : aiPct >= 95 ? 'var(--red)' : aiPct >= 80 ? 'var(--amber)' : 'var(--text2)',
            }}>
              {aiLimit === 0 ? '—' : aiLimit === Infinity ? `${aiCount ?? '…'} used` : `${aiCount ?? '…'} / ${aiLimit}`}
            </span>
          </div>
          <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 6 }}>
            {limitLabel(aiLimit)}
          </div>
          {aiLimit > 0 && aiLimit !== Infinity && (
            <UsageBar current={aiCount ?? 0} limit={aiLimit} />
          )}
          {aiLimit === 0 && (
            <div style={{ fontSize: 11, color: 'var(--text3)', fontStyle: 'italic' }}>
              Upgrade to Professional to unlock 50 AI questions per month.
            </div>
          )}
        </div>

        {/* Document Analyses */}
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 2 }}>
            <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--text)' }}>Document Analyses</span>
            <span style={{
              fontSize: 12, fontWeight: 600,
              color: docLimit === 0 ? 'var(--text3)' : docPct >= 95 ? 'var(--red)' : docPct >= 80 ? 'var(--amber)' : 'var(--text2)',
            }}>
              {docLimit === 0 ? '—' : docLimit === Infinity ? `${docCount ?? '…'} used` : `${docCount ?? '…'} / ${docLimit}`}
            </span>
          </div>
          <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 6 }}>
            {limitLabel(docLimit)}
          </div>
          {docLimit > 0 && docLimit !== Infinity && (
            <UsageBar current={docCount ?? 0} limit={docLimit} />
          )}
          {docLimit === 0 && (
            <div style={{ fontSize: 11, color: 'var(--text3)', fontStyle: 'italic' }}>
              Upgrade to Professional to unlock 5 document analyses per month.
            </div>
          )}
        </div>
      </div>

      {/* Reset info + upgrade link */}
      <div style={{
        background: 'var(--surface)', border: '1px solid var(--border)',
        borderRadius: 8, padding: '12px 16px', marginBottom: 14,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
      }}>
        <div>
          <div style={{ fontSize: 12, fontWeight: 500, color: 'var(--text)', marginBottom: 2 }}>
            Usage resets on {resetDate}
          </div>
          <div style={{ fontSize: 11, color: 'var(--text3)' }}>
            Monthly limits apply per account, not per user.
          </div>
        </div>
        <span style={{
          fontSize: 10, fontFamily: 'var(--mono)', fontWeight: 700,
          padding: '3px 9px', borderRadius: 10, textTransform: 'uppercase',
          color: 'var(--accent)', background: 'var(--blue-bg)',
          border: '1px solid rgba(45,125,210,0.2)',
        }}>{plan}</span>
      </div>

      {plan !== 'business' && (
        <div style={{
          background: 'var(--blue-bg)', border: '1px solid rgba(45,125,210,0.2)',
          borderRadius: 8, padding: '14px 16px',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
        }}>
          <div>
            <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--accent)', marginBottom: 2 }}>
              Need more? Upgrade your plan
            </div>
            <div style={{ fontSize: 11, color: 'var(--text3)' }}>
              Professional: 50 AI questions · 5 docs &nbsp;·&nbsp; Business: 200 AI questions · 20 docs
            </div>
          </div>
          {onUpgrade && (
            <button
              onClick={onUpgrade}
              style={{
                padding: '7px 16px', borderRadius: 6, fontSize: 12, fontWeight: 600,
                cursor: 'pointer', border: '1px solid var(--accent)',
                background: 'var(--accent)', color: '#fff', fontFamily: 'var(--font)',
                flexShrink: 0, whiteSpace: 'nowrap',
              }}
            >
              View Plans
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// ── Main AccountSettings ──────────────────────────────────────────────────────
export default function AccountSettings({ session, profile, currentPlan, onSave, onUpgrade }) {
  const [tab, setTab] = useState('profile');

  const TABS = [
    { id: 'profile',       label: 'Profile'       },
    { id: 'subscription',  label: 'Subscription'  },
    { id: 'usage',         label: 'Usage'         },
    { id: 'security',      label: 'Security'      },
    { id: 'notifications', label: 'Notifications' },
  ];

  return (
    <div style={{ padding: 28, overflowY: 'auto', height: '100%' }}>
      {/* Header */}
      <div style={{ marginBottom: 20 }}>
        <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 3 }}>Account Settings</div>
        <div style={{ fontSize: 12, color: 'var(--text3)' }}>
          Manage your profile, subscription, security, and notification preferences
        </div>
      </div>

      {/* Tabs */}
      <div style={{
        display: 'flex', gap: 4, marginBottom: 22,
        borderBottom: '1px solid var(--border)',
      }}>
        {TABS.map(t => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            style={{
              fontSize: 12, fontWeight: tab === t.id ? 600 : 400,
              padding: '9px 16px', borderRadius: '7px 7px 0 0',
              cursor: 'pointer', fontFamily: 'var(--font)',
              background: tab === t.id ? 'var(--bg2)' : 'transparent',
              color: tab === t.id ? 'var(--accent)' : 'var(--text3)',
              border: tab === t.id ? '1px solid var(--border)' : '1px solid transparent',
              borderBottom: tab === t.id ? '1px solid var(--bg2)' : '1px solid transparent',
              marginBottom: tab === t.id ? -1 : 0,
              transition: 'all 0.15s',
            }}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      {tab === 'profile'       && <ProfileTab       session={session} profile={profile} onSave={onSave} />}
      {tab === 'subscription'  && <SubscriptionTab  session={session} currentPlan={currentPlan} onUpgrade={onUpgrade} />}
      {tab === 'usage'         && <UsageTab         session={session} currentPlan={currentPlan} onUpgrade={onUpgrade} />}
      {tab === 'security'      && <SecurityTab      session={session} />}
      {tab === 'notifications' && <NotificationsTab session={session} />}
    </div>
  );
}
